/**
 * Repositorio de lectura de catálogo — listado admin + búsqueda POS.
 *
 * Módulo profundo: unifica SQL queries, in-memory fallback web, y remote snapshot mapping.
 * Filtros in-memory en `domain/catalogFilter.ts`. SQL helpers en `productCatalogFilterSql.ts`.
 */
import { sellByWeightFromUnit } from '@shared/quantity';
import type { Product } from '../domain/productTypes';
import type { CatalogItemTypeFilter } from '../domain/productTypes';
import type {
  CatalogFilter,
  CatalogProductRow,
  CatalogSort,
  ProductQueryParams,
  ProductSearchRow,
} from '../domain/catalogTypes';
import {
  applyCatalogFiltersInMemory,
  sortCatalogRows,
  type CatalogFilterParams,
} from '../domain/catalogFilter';
import { fetchCatalogComboMetrics } from './comboMetricsRepo';
import { fetchProductCombos } from './productCombosRepo';
import type { ProductCombo } from '../domain/productCombos';
import {
  appendBrandFilter,
  appendCategoryFilter,
  appendIncompleteFilter,
  appendItemTypeFilter,
  appendStatusFilter,
  appendAlertFilter,
  appendSupplierFilter,
  buildCatalogStockJoin,
  catalogSearchFields,
} from './productCatalogFilterSql';
import { appendSearchTokenClauses, normalizeSearchInput, tokenizeSearchInput } from './productSqlSearchNormalize';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization'
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { getCatalogRamSnapshotCache } from './catalogRamSnapshotCache';
import { useAuthStore } from '../../auth/store/useAuthStore';
import {
  aggregateRemoteStocksByProduct,
  loadWebCatalogSnapshotRows,
  type RemoteSnapshotProductRow,
  type RemoteSnapshotStockRow,
} from './productCatalogRemoteSnapshot';
import { getCatalogProductStockSignal } from '../domain/catalogProductStockSignal';
import { getProductCompletenessIssues } from '../domain/catalogProductCompleteness';
// ─── Remote snapshot mapping ────────────────────────────────────────────────

const trimOrNull = (value: unknown): string | null =>
  value != null && String(value).trim() !== '' ? String(value).trim() : null;

/**
 * Nombre de marca para el row: FK `brand_id` resuelta contra el snapshot
 * (fuente de verdad) con fallback a la columna de texto legada `brand`.
 */
const resolveRemoteBrandName = (row: RemoteSnapshotProductRow): string | null => {
  const brandId = trimOrNull(row.brand_id);
  if (brandId) {
    const brands = getCatalogRamSnapshotCache()?.brands ?? [];
    const hit = brands.find((b) => String(b.id) === brandId && b.deleted_at == null);
    const name = trimOrNull(hit?.name);
    if (name) return name;
  }
  return trimOrNull(row.brand);
};

const mapRemoteSnapshotCoreFields = (row: RemoteSnapshotProductRow) => ({
  id: row.id,
  name: row.name ?? '',
  price: Number(row.price ?? 0),
  sku: String(row.sku ?? '').trim() || String(row.barcode ?? '').trim(),
  barcode: trimOrNull(row.barcode),
  itemType: row.item_type === 'service' ? ('service' as const) : ('physical' as const),
  isMisc: row.is_misc === true,
  organizationId: row.organization_id != null ? String(row.organization_id) : null,
  isActive: row.is_active !== false,
  createdAt: row.created_at ?? null,
  updatedAt: row.updated_at ?? null,
  brandId: trimOrNull(row.brand_id),
  brandName: resolveRemoteBrandName(row),
  preferredSupplierId: trimOrNull(row.preferred_supplier_id),
  menuAvailable: row.menu_available !== false,
  visibleOnMenuCarta: row.visible_on_menu_carta !== false,
  isPublic: row.is_public !== false,
  kitchenDestination:
    (row.kitchen_destination === 'bar' ? 'bar' :
     row.kitchen_destination === 'none' ? 'none' :
     'kitchen') as Product['kitchenDestination'],
  prepMinutes:
    row.prep_minutes != null && Number.isFinite(Number(row.prep_minutes))
      ? Number(row.prep_minutes)
      : null,
  comboValidFrom: trimOrNull(row.combo_valid_from),
  comboValidUntil: trimOrNull(row.combo_valid_until),
});

async function loadRemoteCatalogListContext(options?: {
  branchId?: string | null;
  weightedAvgCost?: boolean;
}): Promise<{
  products: RemoteSnapshotProductRow[];
  stockByProduct: Map<string, RemoteSnapshotStockRow>;
} | null> {
  const { session } = useAuthStore.getState();
  if (!session) return null;

  const snapshot = await loadWebCatalogSnapshotRows();
  if (!snapshot) return null;

  const branchId = options?.branchId ?? null;
  const stockByProduct = branchId
    ? aggregateRemoteStocksByProduct(branchId, snapshot.stocks, {
        weightedAvgCost: options?.weightedAvgCost === true,
      })
    : new Map<string, RemoteSnapshotStockRow>();

  return { products: snapshot.products, stockByProduct };
}

export function mapRemoteSnapshotToCatalogProductRow(
  row: RemoteSnapshotProductRow,
  stock?: RemoteSnapshotStockRow
): CatalogProductRow {
  return {
    ...mapRemoteSnapshotCoreFields(row),
    categoryId: trimOrNull(row.category_id),
    stockQuantity: stock ? Number(stock.quantity ?? 0) : null,
    stockMinQuantity: stock ? Number(stock.min_quantity ?? 0) : null,
    stockAvgCost: stock ? Number(stock.avg_cost ?? 0) : null,
    lastSaleAt: null,
    internalNotes: trimOrNull(row.internal_notes),
    supplierItemCode: trimOrNull(row.supplier_item_code),
    unitOfMeasure:
      trimOrNull(row.unit_of_measure) != null
        ? (trimOrNull(row.unit_of_measure) as Product['unitOfMeasure'])
        : 'unit',
    sellByWeight: sellByWeightFromUnit(row.unit_of_measure),
    durationMinutes:
      row.duration_minutes != null && Number.isFinite(Number(row.duration_minutes))
        ? Math.floor(Number(row.duration_minutes))
        : null,
    bufferBeforeMinutes:
      row.buffer_before_minutes != null && Number.isFinite(Number(row.buffer_before_minutes))
        ? Math.max(0, Math.floor(Number(row.buffer_before_minutes)))
        : 0,
    bufferAfterMinutes:
      row.buffer_after_minutes != null && Number.isFinite(Number(row.buffer_after_minutes))
        ? Math.max(0, Math.floor(Number(row.buffer_after_minutes)))
        : 0,
    parentProductId: trimOrNull(row.parent_product_id),
    imageUrl: trimOrNull(row.image_url),
    videoUrl: trimOrNull(row.video_url),
  };
}

export function mapRemoteSnapshotToSearchRow(
  row: RemoteSnapshotProductRow,
  stock?: RemoteSnapshotStockRow
): ProductSearchRow {
  const unitOfMeasure =
    trimOrNull(row.unit_of_measure) != null
      ? (trimOrNull(row.unit_of_measure) as Product['unitOfMeasure'])
      : 'unit';
  return {
    ...mapRemoteSnapshotCoreFields(row),
    categoryId: row.category_id,
    supplierItemCode: row.supplier_item_code ?? null,
    imageUrl: trimOrNull(row.image_url),
    videoUrl: trimOrNull(row.video_url),
    unitOfMeasure,
    sellByWeight: sellByWeightFromUnit(unitOfMeasure),
    durationMinutes:
      row.duration_minutes != null && Number.isFinite(Number(row.duration_minutes))
        ? Math.floor(Number(row.duration_minutes))
        : null,
    bufferBeforeMinutes:
      row.buffer_before_minutes != null && Number.isFinite(Number(row.buffer_before_minutes))
        ? Math.max(0, Math.floor(Number(row.buffer_before_minutes)))
        : 0,
    bufferAfterMinutes:
      row.buffer_after_minutes != null && Number.isFinite(Number(row.buffer_after_minutes))
        ? Math.max(0, Math.floor(Number(row.buffer_after_minutes)))
        : 0,
    stockId: stock ? `${stock.branch_id}:${stock.product_id}` : null,
    stockQuantity: stock ? Number(stock.quantity ?? 0) : null,
    stockMinQuantity: stock ? Number(stock.min_quantity ?? 0) : null,
  };
}

/** Combo/receta se filtran in-memory tras enrich (flags no están en SQL). */
function catalogItemTypeForSql(
  itemType: CatalogItemTypeFilter,
): import('./productCatalogFilterSql').CatalogItemTypeSqlFilter {
  if (itemType === 'physical' || itemType === 'service') return itemType;
  return 'all';
}

// ─── Admin list: SQL WHERE builder ──────────────────────────────────────────

function buildCatalogListWhere({
  organizationId,
  search = '',
  status = 'active',
  category = 'all',
  itemType = 'all',
  pinnedProductId = null,
  brandId = null,
  supplierId = null,
  alertFilter = false,
  incompleteFilter = false,
}: CatalogFilterParams & {
  organizationId: string;
  alertFilter?: boolean;
  incompleteFilter?: boolean;
}): { clauses: string[]; params: Array<string | number> } {
  const normalizedSearch = search.trim().toLowerCase();
  const clauses = ['organization_id = ?', 'COALESCE(is_misc, 0) = 0'];
  const params: Array<string | number> = [organizationId];

  appendStatusFilter(clauses, status, '');
  appendCategoryFilter(clauses, params, category, '');
  appendItemTypeFilter(clauses, catalogItemTypeForSql(itemType), '');
  appendBrandFilter(clauses, params, brandId, '');
  appendSupplierFilter(clauses, params, supplierId, '');

  if (alertFilter) appendAlertFilter(clauses, '');
  if (incompleteFilter) appendIncompleteFilter(clauses, '');

  const pinned = pinnedProductId != null && String(pinnedProductId).trim().length > 0;
  if (pinned) {
    clauses.push('id = ?');
    params.push(String(pinnedProductId).trim());
  } else {
    appendSearchTokenClauses(
      clauses,
      params,
      tokenizeSearchInput(normalizedSearch),
      catalogSearchFields('')
    );
  }

  return { clauses, params };
}

async function enrichCatalogRowsWithRecipeFlags(
  organizationId: string,
  rows: CatalogProductRow[],
): Promise<CatalogProductRow[]> {
  if (rows.length === 0) return rows;
  const { getRecipeProductIds } = await import('./recipeProductIdsRepo');
  const recipeIds = await getRecipeProductIds(organizationId);
  if (recipeIds.size === 0) return rows;
  return rows.map((row) =>
    recipeIds.has(row.id) ? { ...row, hasRecipe: true as const } : row,
  );
}

async function enrichCatalogRowsWithComboMetrics(
  branchId: string | null,
  rows: CatalogProductRow[],
): Promise<CatalogProductRow[]> {
  if (rows.length === 0) return rows;
  const productIds = rows.map((r) => r.id);

  // BOM en product_combo_components: marcar combo aunque falle catalog_combo_metrics (ERP: no usar qty del padre).
  let comboByProductId = new Map<string, ProductCombo>();
  try {
    comboByProductId = await fetchProductCombos(productIds);
  } catch (e) {
    console.warn('[catalog] list_product_combos enrich', e);
  }

  let withComboFlag = rows.map((row) =>
    comboByProductId.has(row.id) ? { ...row, isCombo: true as const } : row,
  );

  if (!branchId) return withComboFlag;

  const metrics = await fetchCatalogComboMetrics(branchId, productIds);
  if (Object.keys(metrics).length === 0) return withComboFlag;

  return withComboFlag.map((row) => {
    const m = metrics[row.id];
    if (!m) return row;
    return {
      ...row,
      isCombo: true,
      comboReferenceUnitCost: m.referenceUnitCost,
      comboReferenceComplete: m.referenceComplete,
      comboSellableQty: m.sellableQty,
    };
  });
}

// ─── Admin list: remote fallback ────────────────────────────────────────────

async function listCatalogProductsRemoteFallback({
  organizationId,
  search = '',
  status = 'active',
  category = 'all',
  itemType = 'all',
  sortBy = 'updated_desc',
  limit,
  offset = 0,
  branchId = null,
  pinnedProductId = null,
  brandId = null,
  supplierId = null,
  alertFilter = false,
  incompleteFilter = false,
}: CatalogFilterParams & {
  organizationId: string;
  sortBy?: CatalogSort;
  limit?: number;
  offset?: number;
  branchId?: string | null;
  alertFilter?: boolean;
  incompleteFilter?: boolean;
}): Promise<CatalogProductRow[]> {
  const ctx = await loadRemoteCatalogListContext({ branchId, weightedAvgCost: true });
  if (!ctx) return [];

  let mapped: CatalogProductRow[] = ctx.products.map((row) =>
    mapRemoteSnapshotToCatalogProductRow(row, branchId ? ctx.stockByProduct.get(row.id) : undefined)
  );
  mapped = await enrichCatalogRowsWithComboMetrics(branchId, mapped);
  mapped = await enrichCatalogRowsWithRecipeFlags(organizationId, mapped);

  const filtered = applyCatalogFiltersInMemory(mapped, {
    organizationId,
    search,
    status,
    category,
    itemType,
    pinnedProductId,
    brandId,
    supplierId,
  });

  // Alert/incomplete filters (server-side equivalent for web fallback)
  let alertFiltered = filtered;
  if (alertFilter) {
    alertFiltered = alertFiltered.filter((p) => {
      if (!p.isActive || p.itemType === 'service' || p.isMisc) return false;
      const { tone } = getCatalogProductStockSignal(p);
      return tone === 'warning' || tone === 'danger';
    });
  }
  if (incompleteFilter) {
    alertFiltered = alertFiltered.filter((p) => getProductCompletenessIssues(p).length > 0);
  }

  const sorted = sortCatalogRows(alertFiltered, sortBy);
  if (typeof limit === 'number') {
    return sorted.slice(Math.max(0, offset), Math.max(0, offset) + limit);
  }
  return sorted;
}

// ─── Admin list: count + list ───────────────────────────────────────────────

export async function countCatalogProducts({
  search = '',
  status = 'active',
  category = 'all',
  itemType = 'all',
  pinnedProductId = null,
  brandId = null,
  supplierId = null,
  alertFilter = false,
  incompleteFilter = false,
}: {
  search?: string;
  status?: CatalogFilter;
  category?: string | 'all';
  itemType?: CatalogItemTypeFilter;
  pinnedProductId?: string | null;
  brandId?: string | null;
  supplierId?: string | null;
  alertFilter?: boolean;
  incompleteFilter?: boolean;
} = {}): Promise<number> {
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return 0;
  const branchId = useSessionStore.getState().currentBranchId ?? null;
        const rows = await listCatalogProductsRemoteFallback({
          organizationId,
          search,
          status,
          category,
          itemType,
          limit: undefined,
          offset: 0,
          branchId,
          pinnedProductId,
          brandId,
          supplierId,
          alertFilter,
          incompleteFilter,
        });
        return rows.length;
}

export async function countAllCatalogProducts(): Promise<number> {
  return countCatalogProducts({ search: '', status: 'active', category: 'all', itemType: 'all' });
}

export async function listCatalogProducts({
  search = '',
  status = 'active',
  category = 'all',
  itemType = 'all',
  sortBy = 'updated_desc',
  limit,
  offset = 0,
  pinnedProductId = null,
  brandId = null,
  supplierId = null,
  alertFilter = false,
  incompleteFilter = false,
}: {
  search?: string;
  status?: CatalogFilter;
  category?: string | 'all';
  itemType?: CatalogItemTypeFilter;
  sortBy?: CatalogSort;
  limit?: number;
  offset?: number;
  pinnedProductId?: string | null;
  brandId?: string | null;
  supplierId?: string | null;
  alertFilter?: boolean;
  incompleteFilter?: boolean;
} = {}): Promise<CatalogProductRow[]> {
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return [];
  const branchId = useSessionStore.getState().currentBranchId ?? null;
  return listCatalogProductsRemoteFallback({
          organizationId,
          search,
          status,
          category,
          itemType,
          sortBy,
          limit,
          offset,
          branchId,
          pinnedProductId,
          brandId,
          supplierId,
          alertFilter,
          incompleteFilter,
        });
}

// ─── Service products picker (form de servicio: producto de evaluación) ──────

export type ServiceProductPickerOption = {
  id: string;
  name: string;
  bookingMode: 'direct' | 'quote_required';
};

const toBookingMode = (v: string | null | undefined): 'direct' | 'quote_required' =>
  v === 'quote_required' ? 'quote_required' : 'direct';

/**
 * Trae todos los servicios activos para el picker de "producto de evaluación"
 * del form de servicio. No depende de la lista paginada/filtrada del catálogo
 * (que puede no incluir el servicio objetivo).
 */
export async function listServiceProductsForPicker(): Promise<ServiceProductPickerOption[]> {
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) return [];

  const snapshot = await loadWebCatalogSnapshotRows();
        if (!snapshot) return [];
        return snapshot.products
          .filter(
            (p) =>
              p.item_type === 'service' &&
              p.is_active !== false &&
              p.organization_id === organizationId,
          )
          .map((p) => ({
            id: p.id,
            name: p.name ?? '',
            bookingMode: toBookingMode(p.booking_mode),
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
}

// ─── POS search: SQL WHERE builder ──────────────────────────────────────────

const PRODUCT_LIMIT = 60;

export const BRANCH_STOCK_JOIN_SQL = `LEFT JOIN (
         SELECT
           MIN(s.id) AS id,
           s.product_id,
           SUM(s.quantity) AS quantity,
           MAX(s.min_quantity) AS min_quantity
         FROM stocks s
         WHERE s.branch_id = ? AND s.variant_id IS NULL
         GROUP BY s.product_id
       ) stock ON stock.product_id = p.id`;

const POS_COL_PREFIX = 'p.';

const matchesSearchTokens = (row: ProductSearchRow, search: string): boolean => {
  const tokens = tokenizeSearchInput(search);
  if (tokens.length === 0) return true;
  // H4: Include variant SKU/barcode in the search haystack so that searching
  // by a variant's code finds the parent product in the catalog list.
  const variantCodes = (getCatalogRamSnapshotCache()?.productVariants ?? [])
    .filter((v) => String(v.product_id) === String(row.id) && v.is_active !== false)
    .map((v) => `${v.sku ?? ''} ${v.barcode ?? ''}`)
    .join(' ');
  const haystack = normalizeSearchInput(
    `${row.name} ${row.sku ?? ''} ${row.barcode ?? ''} ${row.brandName ?? ''} ${variantCodes}`
  );
  return tokens.every((token) => haystack.includes(token));
};

const matchesPosItemType = (
  itemType: string | null | undefined,
  posItemType: ProductQueryParams['posItemType'] = 'all',
  isCombo = false,
) => {
  if (!posItemType || posItemType === 'all') return true;
  if (posItemType === 'service') return itemType === 'service';
  return itemType == null || itemType === 'physical';
};

type ProductSearchFilter = {
  clauses: string[];
  params: Array<string | number>;
  stockJoin: string;
};

function buildProductSearchFilter({
  organizationId,
  categoryId = 'all',
  search = '',
  includeInactive = false,
  branchId = null,
  inventoryPhysicalOnly = false,
  posExcludeMisc = false,
  posItemType = 'all',
  posPhysicalStockPositive = false,
  brandId = null,
  supplierId = null,
}: Omit<ProductQueryParams, 'limit' | 'offset'> & { organizationId: string }): ProductSearchFilter {
  const normalizedSearch = search.trim().toLowerCase();
  const clauses = ['p.organization_id = ?'];
  const params: Array<string | number> = [];

  if (branchId) {
    params.push(branchId);
  }

  params.push(organizationId);

  if (posExcludeMisc) {
    clauses.push('COALESCE(p.is_misc, 0) = 0');
  }

  if (inventoryPhysicalOnly) {
    clauses.push('COALESCE(p.is_misc, 0) = 0');
    clauses.push("(COALESCE(p.item_type, 'physical') = 'physical')");
  }

  appendItemTypeFilter(clauses, posItemType, POS_COL_PREFIX);
  appendStatusFilter(clauses, includeInactive ? 'all' : 'active', POS_COL_PREFIX);
  appendCategoryFilter(clauses, params, categoryId ?? 'all', POS_COL_PREFIX);
  appendBrandFilter(clauses, params, brandId, POS_COL_PREFIX);
  appendSupplierFilter(clauses, params, supplierId, POS_COL_PREFIX);

  const searchTokens = tokenizeSearchInput(normalizedSearch);
  if (searchTokens.length > 0) {
    appendSearchTokenClauses(clauses, params, searchTokens, catalogSearchFields(POS_COL_PREFIX));
  }

  if (branchId && posPhysicalStockPositive) {
    clauses.push(
      "((COALESCE(p.item_type, 'physical') = 'service') OR (COALESCE(stock.quantity, 0) > 0))"
    );
  }

  const stockJoin = branchId ? BRANCH_STOCK_JOIN_SQL : '';

  return { clauses, params, stockJoin };
}

// ─── POS search: remote fallback ────────────────────────────────────────────

async function searchProductsRemoteFallback({
  categoryId = 'all',
  search = '',
  limit = PRODUCT_LIMIT,
  offset = 0,
  includeInactive = false,
  branchId = null,
  inventoryPhysicalOnly = false,
  posExcludeMisc = false,
  posItemType = 'all',
  posPhysicalStockPositive = false,
  brandId = null,
  supplierId = null,
  posProductIds = null,
}: ProductQueryParams = {}): Promise<ProductSearchRow[]> {
  const orgId = await getSessionOrganizationId();
  if (!orgId) return [];

  const ctx = await loadRemoteCatalogListContext({ branchId });
  if (!ctx) return [];

  const { products, stockByProduct } = ctx;
  const categoryFilter = categoryId === 'all' ? null : categoryId;

  const brandIdTrim =
    brandId != null && String(brandId).trim() !== '' ? String(brandId).trim() : '';
  const supplierTrim =
    supplierId != null && String(supplierId).trim() !== '' ? String(supplierId).trim() : '';

  let mappedRows = products
    .filter((p) => p?.id && String(p.organization_id ?? '').trim() === orgId)
    .filter((p) => (posExcludeMisc ? p.is_misc !== true : true))
    .filter((p) =>
      inventoryPhysicalOnly
        ? p.is_misc !== true && (p.item_type == null || p.item_type === 'physical')
        : true
    )
    .map((p) => mapRemoteSnapshotToSearchRow(p, branchId ? stockByProduct.get(p.id) : undefined));

  if (mappedRows.length > 0) {
    const searchIds = mappedRows.map((r) => r.id);
    try {
      const comboMap = await fetchProductCombos(searchIds);
      mappedRows = mappedRows.map((row) =>
        comboMap.has(row.id) ? { ...row, isCombo: true as const } : row,
      );
    } catch (e) {
      console.warn('[catalog] list_product_combos POS enrich', e);
    }
    if (branchId) {
      const metrics = await fetchCatalogComboMetrics(branchId, searchIds);
      mappedRows = mappedRows.map((row) => {
        const m = metrics[row.id];
        if (!m) return row;
        return { ...row, isCombo: true, comboSellableQty: m.sellableQty };
      });
    }
  }

  const filtered = mappedRows
    .filter((row) => matchesPosItemType(row.itemType, posItemType, row.isCombo === true))
    .filter((row) => (includeInactive ? true : row.isActive))
    .filter((row) =>
      categoryFilter
        ? row.categoryId === categoryFilter
        : true
    )
    .filter((row) => matchesSearchTokens(row, search))
    .filter((row) =>
      !brandIdTrim ||
      (row.brandId != null && row.brandId === brandIdTrim)
    )
    .filter((row) => !supplierTrim || row.preferredSupplierId === supplierTrim)
    .filter((row) => {
      if (!posPhysicalStockPositive || !branchId) return true;
      if (row.itemType === 'service') return true;
      if (row.isCombo) {
        return row.comboSellableQty != null && Number(row.comboSellableQty) > 0;
      }
      return row.stockQuantity != null && Number(row.stockQuantity) > 0;
    })
    .filter((row) => (inventoryPhysicalOnly ? !row.isCombo : true))
    .filter((row) => (posProductIds ? posProductIds.has(String(row.id)) : true))
    .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));

  return filtered.slice(offset, offset + limit);
}

// ─── POS search: count + search ─────────────────────────────────────────────

export async function countSearchProducts(
  params: Omit<ProductQueryParams, 'limit' | 'offset'> = {}
): Promise<number> {
  const rows = await searchProductsRemoteFallback({ ...params, limit: Number.MAX_SAFE_INTEGER, offset: 0 });
        return rows.length;
}

export async function searchProducts({
  categoryId = 'all',
  search = '',
  limit = PRODUCT_LIMIT,
  offset = 0,
  includeInactive = false,
  branchId = null,
  inventoryPhysicalOnly = false,
  posExcludeMisc = false,
  posItemType = 'all',
  posPhysicalStockPositive = false,
  brandId = null,
  supplierId = null,
  posProductIds = null,
}: ProductQueryParams = {}): Promise<ProductSearchRow[]> {
  return searchProductsRemoteFallback({
          categoryId,
          search,
          limit,
          offset,
          includeInactive,
          branchId,
          inventoryPhysicalOnly,
          posExcludeMisc,
          posItemType,
          posPhysicalStockPositive,
          brandId,
          supplierId,
          posProductIds,
        });
}

/** CAT-S1: Devuelve un mapa productId -> kitchen_destination para filtrar l\u00edneas en POS. */
export async function getKitchenDestinationMap(productIds: string[]): Promise<Map<string, 'kitchen' | 'bar' | 'none'>> {
  // ADR-017 Fase 4: SQLite local eliminado — leer de catalogRamSnapshotCache.
  const result = new Map<string, 'kitchen' | 'bar' | 'none'>();
  if (productIds.length === 0) return result;
  const products = getCatalogRamSnapshotCache()?.products ?? [];
  const idSet = new Set(productIds);
  for (const p of products) {
    if (!idSet.has(String(p.id))) continue;
    const dest = p.kitchen_destination;
    if (dest === 'bar' || dest === 'none' || dest === 'kitchen') {
      result.set(String(p.id), dest);
    }
  }
  return result;
}